import chunk from "lodash/chunk";
import flatten from "lodash/flatten";
import uniq from "lodash/uniq";
import { WSQueryCache, WSQueryConfig } from "@ws-react-query";
import { getPayeeRows, IPayeeRow } from "../../../../services/search";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import {
  concurrentActions,
  WSServiceError
} from "../../../../utils/serviceHelper";
import { useWSQuery } from "../../../helpers";

// One `payeeId.in` request stays well under URL length limits at this size,
// and the results of a single id-batch fit in one response page.
const BATCH_SIZE = 100;

export const QUERY_ALL_PAYEE_ROWS_BY_IDS = "QUERY_ALL_PAYEE_ROWS_BY_IDS";
export const QUERY_PAYEE_ROW_BY_ID = "QUERY_PAYEE_ROW_BY_ID";

const cacheKeyForId = (payeeId: string, xWingspanExpansion?: string) => [
  QUERY_PAYEE_ROW_BY_ID,
  payeeId,
  xWingspanExpansion
];

export const fetchAllPayeeRowsByIds = async (
  payeeIds: string[],
  xWingspanExpansion?: string
): Promise<IPayeeRow[]> => {
  const ids = uniq(payeeIds);

  const cachedById = new Map<string, IPayeeRow>();
  const missingIds: string[] = [];

  for (const payeeId of ids) {
    const cached = WSQueryCache.getQueryData<IPayeeRow>(
      cacheKeyForId(payeeId, xWingspanExpansion)
    );

    if (cached) {
      cachedById.set(payeeId, cached);
    } else {
      missingIds.push(payeeId);
    }
  }

  if (missingIds.length > 0) {
    const actions = chunk(missingIds, BATCH_SIZE).map(
      batch => () =>
        getPayeeRows(
          {
            filter: { payeeId: { in: batch } },
            page: { size: BATCH_SIZE, number: 1 }
          },
          xWingspanExpansion
        )
    );

    const fetchedRows = flatten(
      await concurrentActions(actions, { concurrentLimit: 5 })
    );

    for (const row of fetchedRows) {
      WSQueryCache.setQueryData(
        cacheKeyForId(row.payeeId, xWingspanExpansion),
        row
      );
      cachedById.set(row.payeeId, row);
    }
  }

  // Preserve the caller's id order and drop ids the search service returned no
  // row for (e.g. stale payeeId on a payable).
  return ids
    .map(payeeId => cachedById.get(payeeId))
    .filter((row): row is IPayeeRow => Boolean(row));
};

/**
 * Resolves payee rows for a set of payee ids, hitting the per-id cache first
 * and only requesting the ids that miss. Use this over `useQueryAllPayeeRows`
 * when you already have the ids and just need the matching rows.
 */
export const useQueryAllPayeeRowsByIds = (
  payeeIds: string[],
  config?: WSQueryConfig<IPayeeRow[], WSServiceError>
) => {
  const xWingspanExpansion = useXWingspanExpansion();
  const ids = uniq(payeeIds).sort();

  return useWSQuery<IPayeeRow[], WSServiceError>(
    [QUERY_ALL_PAYEE_ROWS_BY_IDS, ids, xWingspanExpansion],
    async () => {
      return fetchAllPayeeRowsByIds(ids, xWingspanExpansion);
    },
    {
      staleTime: Infinity,
      refetchOnMount: false,
      ...config
    }
  );
};
